import { gameWeek } from "./gameWeek";
import { gameDay } from "./gameDay";
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { teamAudienceRankings, sortTeamAudienceRankings } from "./teamAudienceRankings";
import "./ViewershipRankings.css";
import BACKEND_BASE from "./config";
import { getTeamLogoUrl } from "./teamLogos";

function formatMillions(value) {
  if (value == null) {
    return "N/A";
  }

  return `${(value / 1000).toFixed(2)}M`;
}

function FilterSelect({ label, value, onChange, options }) {
  return (
    <div className="scenario-select-block">
      <label className="scenario-select-label" htmlFor={`rankings-${label.replaceAll(" ", "-").toLowerCase()}`}>{label}</label>
      <select id={`rankings-${label.replaceAll(" ", "-").toLowerCase()}`} value={value} onChange={(e) => onChange(e.target.value)}>
        {options.map((option) => (
          <option key={option} value={option}>
            {option === "all" ? "All" : option}
          </option>
        ))}
      </select>
    </div>
  );
}

export default function ViewershipRankings({ view = "games" }) {
  return <RankingsBoard key={view} view={view} />;
}

function RankingsBoard({ view }) {
  const [sort, setSort] = useState({ key: 'average', direction: 'desc' });
  function changeSort(key) {
    setSort(current => ({ key, direction: current.key === key
      ? (current.direction === 'asc' ? 'desc' : 'asc')
      : (['team', 'conference', 'rank'].includes(key) ? 'asc' : 'desc') }));
  }
  const [minGames, setMinGames] = useState(1);
  const [filters, updateFilters] = useState({
    season: "all",
    season_type: "regular",
    conference: "all",
    conference2: "all",
    network: "all",
    time_bucket: "all",
    rank_bucket: "all",
    team: "all",
    team2: "all",
    include_conf_champ: true,
  });
  const [offset, setOffset] = useState(0);
  function setFilters(update) {
    setOffset(0);
    updateFilters(update);
  }
  const [data, setData] = useState({
    rows: [],
    available_filters: {
      networks: ["all"],
      time_buckets: ["all"],
      rank_buckets: ["all"],
    },
  });
  const [loading, setLoading] = useState(false);
  const [retry, setRetry] = useState(0);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    const controller = new AbortController();
    async function loadRankings() {
      try {
        if (offset === 0) setLoading(true);
        else setLoadingMore(true);
        setError("");

        const params = new URLSearchParams({
          season: filters.season,
          season_type: filters.season_type,
          conference: filters.conference,
          conference2: filters.conference2,
          network: filters.network,
          time_bucket: filters.time_bucket,
          rank_bucket: filters.rank_bucket,
          team: filters.team,
          team2: filters.team2,
          include_conf_champ: String(filters.include_conf_champ),
        });
        if (view === "games") {
          params.set("limit", "50");
          params.set("offset", String(offset));
        }
        const res = await fetch(`${BACKEND_BASE}/game-viewership-rankings?${params.toString()}`, { signal: controller.signal });
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const payload = await res.json();
        if (controller.signal.aborted) return;
        setData(previous => ({ ...payload, rows: offset === 0 ? payload.rows : [...previous.rows, ...payload.rows] }));
      } catch (err) {
        if (controller.signal.aborted) return;
        console.error("Viewership rankings load error:", err);
        setError("Failed to load game viewership rankings.");
      } finally {
        if (!controller.signal.aborted) { setLoading(false); setLoadingMore(false); }
      }
    }

    loadRankings();
    return () => controller.abort();
  }, [filters, offset, view, retry]);

  const teamRows = view !== "teams" ? [] : sortTeamAudienceRankings(teamAudienceRankings(data.rows || [], { ...filters, minGames, allowedTeams: data.available_filters?.teams?.filter(t => t !== "all") }), sort.key, sort.direction);

  return (
    <div>
      <h2 className="text-3xl font-semibold mb-4">{view === "teams" ? "Team Viewership Rankings" : "Viewership Rankings"}</h2>
      <p className="text-gray-600 mb-3 max-w-4xl">
        {view === "teams" ? "Compare total, average and median reported viewership for games matching your filters. Click a column header to sort." : "Rank every rated college football game by viewership, then filter the board by season, network, team, rankings, and TV window."}
      </p>
      <p className="text-gray-500 text-sm mb-6 max-w-4xl">
        {view === "teams" ? "Total viewership sums reported broadcast audiences; it is not a count of unique viewers. Each matching broadcast counts once for each participating team. Unreported audiences are excluded. Conference filters select teams in that conference." : "Conference filters match either team. Choose the same conference twice for games within that conference."}
      </p>

      <div className="scenario-controls mb-6">
        <div className="scenario-select-block"><label className="scenario-select-label" htmlFor="rankings-season-type">Game type</label><select id="rankings-season-type" value={filters.season_type} onChange={e => setFilters(prev => ({...prev, season_type: e.target.value}))}><option value="regular">Regular season</option><option value="all">Regular season + postseason</option><option value="postseason">Bowls & playoffs</option></select></div>
        {view === "teams" && <div className="scenario-select-block"><label className="scenario-select-label" htmlFor="minimum-rated-games">Minimum games</label><input id="minimum-rated-games" type="number" min="1" step="1" value={minGames} onChange={e => setMinGames(Math.max(1, Math.floor(Number(e.target.value) || 1)))} /></div>}
        <FilterSelect
          label="Season"
          value={filters.season}
          onChange={(value) => setFilters((prev) => ({ ...prev, season: value }))}
          options={data.available_filters?.seasons || ["all"]}
        />
        <FilterSelect
          label={view === "games" ? "Conference 1" : "Conference"}
          value={filters.conference}
          onChange={(value) => setFilters((prev) => ({ ...prev, conference: value }))}
          options={data.available_filters?.conferences || ["all"]}
        />
        {view === "games" && <FilterSelect
          label="Conference 2"
          value={filters.conference2}
          onChange={(value) => setFilters((prev) => ({ ...prev, conference2: value }))}
          options={data.available_filters?.conferences || ["all"]}
        />}
        <FilterSelect
          label="Network"
          value={filters.network}
          onChange={(value) => setFilters((prev) => ({ ...prev, network: value }))}
          options={data.available_filters?.networks || ["all"]}
        />
        <FilterSelect
          label="Time Slot"
          value={filters.time_bucket}
          onChange={(value) => setFilters((prev) => ({ ...prev, time_bucket: value }))}
          options={data.available_filters?.time_buckets || ["all"]}
        />
        <FilterSelect
          label="Rankings"
          value={filters.rank_bucket}
          onChange={(value) => setFilters((prev) => ({ ...prev, rank_bucket: value }))}
          options={data.available_filters?.rank_buckets || ["all"]}
        />
        <FilterSelect
          label={view === "games" ? "Team 1" : "Team"}
          value={filters.team}
          onChange={(value) => setFilters((prev) => ({ ...prev, team: value }))}
          options={data.available_filters?.teams || ["all"]}
        />
        {view === "games" && <FilterSelect
          label="Team 2"
          value={filters.team2}
          onChange={(value) => setFilters((prev) => ({ ...prev, team2: value }))}
          options={data.available_filters?.teams || ["all"]}
        />}
      </div>
      <div className="mt-3">
        <label className="team-profile-toggle">
          <input
            type="checkbox"
            checked={filters.include_conf_champ}
            onChange={(e) =>
              setFilters((prev) => ({
                ...prev,
                include_conf_champ: e.target.checked,
              }))
            }
          />
          Include conference championship games
        </label>
      </div>

      {loading && <p>Loading…</p>}
      {error && <p role="alert" className="text-red-600">{error} <button type="button" onClick={() => setRetry(current => current + 1)}>Retry</button></p>}

      {!loading && !error && view === "teams" && (
        <div className="overflow-x-auto">
          <p className="text-gray-500 text-sm my-3">{teamRows.length} teams · {data.rows?.length || 0} matching broadcasts</p>
          <table className="min-w-max w-full team-audience-table">
            <thead><tr>{[
              ['rank', 'Average rank'], ['team', 'Team'], ['conference', 'Conference'],
              ['games', 'Rated games'], ['total', 'Total viewership'],
              ['average', 'Average viewership'], ['median', 'Median viewership'],
            ].map(([key, label]) => (
              <th key={key} scope="col" aria-sort={sort.key === key ? (sort.direction === 'asc' ? 'ascending' : 'descending') : 'none'}>
                <button type="button" className="audience-sort" onClick={() => changeSort(key)}>
                  {label}<svg viewBox="0 0 12 16" width="12" height="16" aria-hidden="true">
                    <path d="M2 6 6 2 10 6" className={sort.key === key && sort.direction === 'asc' ? 'active' : ''} />
                    <path d="m2 10 4 4 4-4" className={sort.key === key && sort.direction === 'desc' ? 'active' : ''} />
                  </svg>
                </button>
              </th>
            ))}</tr></thead>
            <tbody>{teamRows.map(row => <tr key={row.team}><td>{row.rank}</td><td><Link className="matchup-cell team-profile-link" to={`/teams/football/${encodeURIComponent(row.team)}`}>{getTeamLogoUrl(row.team) && <img className="team-logo" src={getTeamLogoUrl(row.team)} alt="" />}{row.team}</Link></td><td>{row.conference}</td><td>{row.games}</td><td>{formatMillions(row.total)}</td><td>{formatMillions(row.average)}</td><td>{formatMillions(row.median)}</td></tr>)}</tbody>
          </table>
          {!teamRows.length && <p className="my-4">No teams match these filters and minimum game count.</p>}
        </div>
      )}
      {!loading && view === "games" && (
        <div className="overflow-x-auto">
          <table className="min-w-max w-full">
            <thead>
              <tr>
                <th>Rank</th>
                <th>Date</th><th>Day</th><th>Week</th>
                <th>Season</th>
                <th>Matchup</th>
                <th>Network</th>
                <th>Time Slot</th>
                <th>Rankings</th>
                <th>Viewership</th>
              </tr>
            </thead>
            <tbody>
              {data.rows?.map((row) => (
                <tr key={`${row.rank}-${row.season}-${row.date}-${row.matchup}`}>
                  <td>{row.rank}</td>
                  <td>{row.date}</td><td>{gameDay(row.date, row.year || row.season)}</td><td>{gameWeek(row)}</td>
                  <td>{row.season}</td>
                  <td>
                    <span className="matchup-cell">
                      <span className="matchup-logos">
                        {[row.team1, row.team2].map((team) => (
                          getTeamLogoUrl(team) ? (
                            <img
                              key={team}
                              src={getTeamLogoUrl(team)}
                              alt={`${team} logo`}
                              className="team-logo"
                            />
                          ) : null
                        ))}
                      </span>
                      <span>
                        <Link to={`/teams/football/${encodeURIComponent(row.team1)}`} className="team-profile-link">
                          {row.team1}
                        </Link>
                        {" vs "}
                        <Link to={`/teams/football/${encodeURIComponent(row.team2)}`} className="team-profile-link">
                          {row.team2}
                        </Link>
                        {row.event && <span className="game-badge">{row.event}</span>}
                        {row.conference_championship && <span className="game-badge">Conf Champ</span>}
                      </span>
                    </span>
                  </td>
                  <td>{row.network}</td>
                  <td>{row.raw_time_slot || row.time_slot}</td>
                  <td>{row.rank_bucket}</td>
                  <td title={row.measurement_note || undefined}>{formatMillions(row.viewers)}{row.source_url && <small className="vw-broadcast-note"><a href={row.source_url} target="_blank" rel="noreferrer">Source</a></small>}</td>
                </tr>
              ))}
            </tbody>
          </table>
          <div className="rankings-pagination">
            <p role="status">Showing {data.rows?.length || 0} of {data.total || 0} games</p>
            {data.next_offset != null && (
              <button type="button" disabled={loadingMore} onClick={() => {
                if (error) setRetry(current => current + 1);
                setOffset(data.next_offset);
              }}>Load 50 more</button>
            )}
            {loadingMore && <span role="status">Loading more games…</span>}
          </div>
        </div>
      )}
    </div>
  );
}
